import React, { useState, useEffect } from 'react';
import { Select, Input, DatePicker, InputNumber } from 'antd';
import dayjs from 'dayjs';
import { format } from 'date-fns';
import { 
  Plus, 
  Search, 
  Filter, 
  Edit, 
  Trash, 
  Calendar, 
  Building2, 
  Tag, 
  IndianRupee,
  X,
  Download,
  Eye,
  Upload
} from 'lucide-react';
import * as XLSX from 'xlsx';
import { 
  fetchExpenses, 
  addExpense, 
  updateExpense, 
  deleteExpense, 
  fetchExpenseCategories,
  fetchExpenseStats
} from '../utils/api';
import { formatIndianCurrency, formatIndianNumber } from '../utils/numberToWords';

function Expenses({ facilities, currentUser }) {
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [stats, setStats] = useState({});
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadFileName, setUploadFileName] = useState('');
  const [parsedRows, setParsedRows] = useState([]);
  const [invalidRows, setInvalidRows] = useState([]);
  const [uploadErrors, setUploadErrors] = useState([]);
  const [isParsing, setIsParsing] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [importProgress, setImportProgress] = useState({ done: 0, total: 0 });
  
  // Filter states
  const [filters, setFilters] = useState({
    facility: '',
    category: '',
    serviceMonth: '',
    search: ''
  });

  // Financial Year and Month filters (defaults: current FY and month)
  const getCurrentFY = () => {
    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth() + 1; // 1-12
    const startY = m >= 4 ? y : y - 1;
    const endY = startY + 1;
    return `${startY}-${endY}`;
  };
  const getCurrentFYMonthKey = () => {
    const m = new Date().getMonth() + 1;
    const map = ['jan','feb','mar','apr','may','jun','jul','aug','sep','oct','nov','dec'];
    return map[m-1];
  };
  const [financialYear, setFinancialYear] = useState(getCurrentFY());
  const [monthKey, setMonthKey] = useState(getCurrentFYMonthKey());

  const getFYOptions = () => {
    const list = [];
    const now = new Date();
    const currentStart = (now.getMonth() + 1) >= 4 ? now.getFullYear() : now.getFullYear() - 1;
    for (let i = 0; i < 6; i++) {
      const s = currentStart - i;
      list.push({ label: `${s}-${s + 1}`, value: `${s}-${s + 1}` });
    }
    return list;
  };
  const MONTHS_FY = [
    { label: 'All Months', value: 'all' },
    { label: 'April', value: 'apr' },
    { label: 'May', value: 'may' },
    { label: 'June', value: 'jun' },
    { label: 'July', value: 'jul' },
    { label: 'August', value: 'aug' },
    { label: 'September', value: 'sep' },
    { label: 'October', value: 'oct' },
    { label: 'November', value: 'nov' },
    { label: 'December', value: 'dec' },
    { label: 'January', value: 'jan' },
    { label: 'February', value: 'feb' },
    { label: 'March', value: 'mar' }
  ];

  const getStartEndFromFYMonth = (fyStr, mKey) => {
    if (!fyStr) return { startDate: '', endDate: '' };
    const parts = String(fyStr).split('-');
    const startY = parseInt(parts[0], 10);
    const endY = parseInt(parts[1], 10);
    if (!mKey || mKey === 'all') {
      return { startDate: `${startY}-04-01`, endDate: `${endY}-03-31` };
    }
    const mapToMonth = {
      apr: { y: startY, m: 4 }, may: { y: startY, m: 5 }, jun: { y: startY, m: 6 }, jul: { y: startY, m: 7 }, aug: { y: startY, m: 8 }, sep: { y: startY, m: 9 }, oct: { y: startY, m: 10 }, nov: { y: startY, m: 11 }, dec: { y: startY, m: 12 },
      jan: { y: endY, m: 1 }, feb: { y: endY, m: 2 }, mar: { y: endY, m: 3 }
    };
    const inf = mapToMonth[mKey];
    if (!inf) return { startDate: '', endDate: '' };
    const sd = `${inf.y}-${String(inf.m).padStart(2, '0')}-01`;
    const lastDay = new Date(inf.y, inf.m, 0).getDate();
    const ed = `${inf.y}-${String(inf.m).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
    return { startDate: sd, endDate: ed };
  };
  
  // Pagination
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 50
  });

  // Form states
  const [formData, setFormData] = useState({
    category: '',
    name: '',
    invoiceDate: '',
    invoiceNumber: '',
    description: '',
    facility: '',
    serviceMonth: '',
    amount: ''
  });

  // Load initial data
  useEffect(() => {
    loadExpenses();
    loadCategories();
  }, []);

  // Load operational costs when filters change
  useEffect(() => {
    loadExpenses();
  }, [filters, pagination.currentPage, financialYear, monthKey]);

  const loadExpenses = async () => {
    try {
      setLoading(true);
      const { startDate, endDate } = getStartEndFromFYMonth(financialYear, monthKey);
      
      // Convert monthKey to service month for more accurate filtering
      const getServiceMonthFromKey = (key) => {
        if (key === 'all') return null;
        const monthMap = {
          'apr': 'apr', 'may': 'may', 'jun': 'jun', 'jul': 'jul', 'aug': 'aug', 'sep': 'sep',
          'oct': 'oct', 'nov': 'nov', 'dec': 'dec', 'jan': 'jan', 'feb': 'feb', 'mar': 'mar'
        };
        return monthMap[key] || null;
      };
      
      const params = {
        ...filters,
        page: pagination.currentPage,
        limit: pagination.itemsPerPage
      };
      
      // Use service month if a specific month is selected, otherwise use date range
      const serviceMonth = getServiceMonthFromKey(monthKey);
      if (serviceMonth) {
        params.serviceMonth = serviceMonth;
        // Remove date filters when using service month
        delete params.startDate;
        delete params.endDate;
      } else if (startDate && endDate) {
        params.startDate = startDate;
        params.endDate = endDate;
      }
      
      const response = await fetchExpenses(params);
      // Normalize operational costs so category is always an object with a name field
      const normalizedExpenses = (response.expenses || []).map((exp) => ({
        ...exp,
        category:
          typeof exp.category === 'string'
            ? { name: exp.category }
            : exp.category || { name: '' },
      }));
      setExpenses(normalizedExpenses);
      setPagination(response.pagination || {
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: 50
      });
      
      // Load stats with same filters
      const statsResponse = await fetchExpenseStats({ ...filters, startDate, endDate });
      setStats(statsResponse || {});
    } catch (error) {
      alert('Failed to load operational costs');
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const categoriesData = await fetchExpenseCategories();
      // Ensure categories are strings for the dropdown
      const normalizedCategories = (categoriesData || []).map((c) =>
        typeof c === 'string' ? c : c?.name || ''
      );
      setCategories(normalizedCategories);
    } catch (error) {
      // Error loading categories
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const resetForm = () => {
    setFormData({
      category: '',
      name: '',
      invoiceDate: '',
      invoiceNumber: '',
      description: '',
      facility: '',
      serviceMonth: '',
      amount: ''
    });
    setEditingExpense(null);
  };

  const openAddModal = () => {
    resetForm();
    setShowAddModal(true);
  };

  const openUploadModal = () => {
    setUploadFileName('');
    setParsedRows([]);
    setInvalidRows([]);
    setUploadErrors([]);
    setImportProgress({ done: 0, total: 0 });
    setShowUploadModal(true);
  };

  const openEditModal = (expense) => {
    setFormData({
      category: expense.category?.name || '',
      name: expense.name || '',
      invoiceDate: expense.invoiceDate ? format(new Date(expense.invoiceDate), 'yyyy-MM-dd') : '',
      invoiceNumber: expense.invoiceNumber || '',
      description: expense.description || '',
      facility: expense.facility || '',
      serviceMonth: expense.serviceMonth || '',
      amount: (expense.amount || 0).toString()
    });
    setEditingExpense(expense);
    setShowAddModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.category || !formData.name || !formData.facility || !formData.amount) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      const expenseData = {
        ...formData,
        amount: parseFloat(formData.amount)
      };

      if (editingExpense) {
        await updateExpense(editingExpense._id, expenseData);
      } else {
        await addExpense(expenseData);
      }

      setShowAddModal(false);
      resetForm();
      loadExpenses();
      alert(editingExpense ? 'Operational cost updated successfully!' : 'Operational cost added successfully!');
    } catch (error) {
      alert('Failed to save expense');
    }
  };

  const handleDelete = async (expenseId) => {
    if (!window.confirm('Are you sure you want to delete this expense?')) {
      return;
    }

    try {
      await deleteExpense(expenseId);
      loadExpenses();
      alert('Operational cost deleted successfully!');
    } catch (error) {
      alert('Failed to delete expense');
    }
  };

  const exportToCSV = () => {
    const headers = ['Category', 'Name', 'Facility', 'Amount', 'Created By', 'Created At'];
    const csvData = expenses.map(expense => [
      expense.category?.name || '',
      expense.name || '',
      expense.facility || '',
      expense.amount || 0,
      expense.createdBy?.name || '',
      expense.createdAt ? format(new Date(expense.createdAt), 'dd/MM/yyyy') : ''
    ]);

    const csvContent = [headers, ...csvData]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `operational_costs_${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const downloadTemplate = () => {
    try {
      const header = ['Category', 'Name', 'Facility', 'Amount', 'Invoice Date', 'Invoice Number', 'Description'];
      const example = ['Travel', 'Cab to client site', facilities?.[0]?.name || 'Main Facility', 1200, '2025-01-31', 'INV-123', 'Uber cab for meeting'];
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.aoa_to_sheet([header, example]);
      XLSX.utils.book_append_sheet(wb, ws, 'Operational Cost');
      const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([wbout], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Operational_Cost_Template.xlsx';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert('Failed to generate template');
    }
  };

  const parseExcelDate = (value) => {
    if (!value) return '';
    if (typeof value === 'string') {
      // Try to parse YYYY-MM-DD or DD/MM/YYYY
      const isoMatch = /^\d{4}-\d{2}-\d{2}$/.test(value.trim());
      if (isoMatch) return value.trim();
      const dmy = value.trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
      if (dmy) {
        const dd = String(dmy[1]).padStart(2, '0');
        const mm = String(dmy[2]).padStart(2, '0');
        const yyyy = String(dmy[3]).length === 2 ? `20${dmy[3]}` : dmy[3];
        return `${yyyy}-${mm}-${dd}`;
      }
      const asDate = new Date(value);
      if (!isNaN(asDate.getTime())) {
        const yyyy = asDate.getFullYear();
        const mm = String(asDate.getMonth() + 1).padStart(2, '0');
        const dd = String(asDate.getDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
      }
      return '';
    }
    if (typeof value === 'number') {
      // Excel serial date
      const serial = value;
      const utcDays = Math.floor(serial - 25569);
      const utcValue = utcDays * 86400; // seconds
      const dateInfo = new Date(utcValue * 1000);
      const yyyy = dateInfo.getUTCFullYear();
      const mm = String(dateInfo.getUTCMonth() + 1).padStart(2, '0');
      const dd = String(dateInfo.getUTCDate()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}`;
    }
    return '';
  };

  const handleFileSelected = async (file) => {
    if (!file) return;
    setIsParsing(true);
    setUploadFileName(file.name);
    setUploadErrors([]);
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: 'array' });
      const sheetName = wb.SheetNames[0];
      const sheet = wb.Sheets[sheetName];
      const rows = XLSX.utils.sheet_to_json(sheet, { defval: '' });
      // Normalize keys to lowercase
      const normalized = rows.map((r) => {
        const lower = {};
        Object.keys(r).forEach((k) => { lower[String(k).toLowerCase().trim()] = r[k]; });
        return {
          category: String(lower['category'] || '').trim(),
          name: String(lower['name'] || '').trim(),
          facility: String(lower['facility'] || '').trim(),
          amount: lower['amount'],
          invoiceDate: parseExcelDate(lower['invoice date'] || lower['invoicedate'] || lower['date']),
          invoiceNumber: String(lower['invoice number'] || lower['invoicenumber'] || '').trim(),
          description: String(lower['description'] || '').trim(),
        };
      });

      // Filter out completely empty rows
      const nonEmpty = normalized.filter(r => r.category || r.name || r.facility || r.amount || r.invoiceDate || r.invoiceNumber || r.description);

      validateParsedRows(nonEmpty);
    } catch (e) {
      setUploadErrors([e.message || 'Failed to parse file']);
      setParsedRows([]);
      setInvalidRows([]);
    } finally {
      setIsParsing(false);
    }
  };

  const validateParsedRows = (rows) => {
    const facilityNames = (facilities || []).map(f => f.name);
    const valid = [];
    const invalid = [];
    rows.forEach((row, idx) => {
      const errors = [];
      if (!row.category) errors.push('Category is required');
      if (!row.name) errors.push('Name is required');
      if (!row.facility) errors.push('Facility is required');
      const amountNum = typeof row.amount === 'number' ? row.amount : parseFloat(String(row.amount).replace(/[,]/g, ''));
      if (isNaN(amountNum) || amountNum <= 0) errors.push('Amount must be a positive number');
      if (row.category && !categories.includes(row.category)) errors.push('Unknown category');
      if (row.facility && !facilityNames.includes(row.facility)) errors.push('Unknown facility');

      const parsed = {
        ...row,
        amount: isNaN(amountNum) ? '' : Number(amountNum),
      };

      if (errors.length) {
        invalid.push({ index: idx + 2, // +2 to account for header row
          row: parsed, errors });
      } else {
        valid.push(parsed);
      }
    });

    setParsedRows(valid);
    setInvalidRows(invalid);
  };

  const onDropUpload = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files && e.dataTransfer.files[0];
    if (file) handleFileSelected(file);
  };

  const onDragOver = (e) => {
    e.preventDefault();
  };

  const startImport = async () => {
    if (!parsedRows.length) {
      alert('No valid rows to import');
      return;
    }
    setIsImporting(true);
    setImportProgress({ done: 0, total: parsedRows.length });
    let success = 0;
    let fail = 0;
    for (let i = 0; i < parsedRows.length; i++) {
      const r = parsedRows[i];
      try {
        await addExpense({
          category: r.category,
          name: r.name,
          facility: r.facility,
          amount: r.amount,
          invoiceDate: r.invoiceDate || '',
          invoiceNumber: r.invoiceNumber || '',
          description: r.description || ''
        });
        success++;
      } catch (e) {
        fail++;
      } finally {
        setImportProgress({ done: i + 1, total: parsedRows.length });
      }
    }
    setIsImporting(false);
    if (fail === 0) {
      alert(`Imported ${success} operational costs successfully`);
    } else {
      alert(`Imported ${success} operational costs, ${fail} failed`);
    }
    setShowUploadModal(false);
    loadExpenses();
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <div className="page-title">
          <h1>Operational Cost Management</h1>
          <p>Track and manage all business operational costs</p>
        </div>
        <div className="page-actions">
          {currentUser?.role !== 'viewer' && (
          <button onClick={openUploadModal} className="btn btn-secondary">
            <Upload size="1.2em" />
            Excel Upload
          </button>
          )}
          <button onClick={exportToCSV} className="btn btn-secondary">
            <Download size="1.2em" />
            Export CSV
          </button>
          {currentUser?.role !== 'viewer' && (
          <button onClick={openAddModal} className="btn btn-primary">
            <Plus size="1.2em" />
            Add Operational Cost
          </button>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon">
            <IndianRupee size="2em" color="#43a047" />
          </div>
          <div className="stat-content">
            <h3>Total Amount</h3>
            <p className="stat-value">{formatIndianCurrency(stats.totalAmount || 0)}</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">
            <Eye size="2em" color="#2196f3" />
          </div>
          <div className="stat-content">
            <h3>Total Operational Cost</h3>
            <p className="stat-value">{formatIndianNumber(stats.totalCount || 0, 0)}</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">
            <Tag size="2em" color="#ff9800" />
          </div>
          <div className="stat-content">
            <h3>Categories</h3>
            <p className="stat-value">{formatIndianNumber((stats.categoryBreakdown?.length) || 0, 0)}</p>
          </div>
        </div>
        {/* Facility KPI removed as requested */}
      </div>

      {/* Filters */}
      <div className="filters-section">
        <div className="filters-header">
          <Filter size="1.2em" />
          <h3>Filters</h3>
        </div>
        <div className="filters-grid">
          <div className="filter-group">
            <label>Search</label>
            <Input
              prefix={<Search size="1.2em" />}
              placeholder="Search operational costs..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              allowClear
            />
          </div>
          
          <div className="filter-group">
            <label>Facility</label>
            <Select
              style={{ width: '100%' }}
              value={filters.facility || undefined}
              onChange={(v) => handleFilterChange('facility', v || '')}
              allowClear
              placeholder="All Facilities"
              options={facilities.map(f => ({ label: f.name, value: f.name }))}
              showSearch
              optionFilterProp="label"
              getPopupContainer={(trigger) => trigger.parentNode}
            />
          </div>
          
          <div className="filter-group">
            <label>Category</label>
            <Select
              style={{ width: '100%' }}
              value={filters.category || undefined}
              onChange={(v) => handleFilterChange('category', v || '')}
              allowClear
              placeholder="All Categories"
              options={categories.map(c => ({ label: c, value: c }))}
              showSearch
              optionFilterProp="label"
              getPopupContainer={(trigger) => trigger.parentNode}
            />
          </div>
          <div className="filter-group">
            <label>Service Month</label>
            <Select
              style={{ width: '100%' }}
              value={filters.serviceMonth || undefined}
              onChange={(v) => handleFilterChange('serviceMonth', v || '')}
              allowClear
              placeholder="All Months"
              options={[
                { label: 'January', value: 'jan' },
                { label: 'February', value: 'feb' },
                { label: 'March', value: 'mar' },
                { label: 'April', value: 'apr' },
                { label: 'May', value: 'may' },
                { label: 'June', value: 'jun' },
                { label: 'July', value: 'jul' },
                { label: 'August', value: 'aug' },
                { label: 'September', value: 'sep' },
                { label: 'October', value: 'oct' },
                { label: 'November', value: 'nov' },
                { label: 'December', value: 'dec' }
              ]}
              getPopupContainer={(trigger) => trigger.parentNode}
            />
          </div>
          <div className="filter-group">
            <label>Financial Year</label>
            <Select
              style={{ width: '100%' }}
              value={financialYear}
              onChange={(v) => setFinancialYear(v)}
              options={getFYOptions()}
              getPopupContainer={(trigger) => trigger.parentNode}
            />
          </div>
          <div className="filter-group">
            <label>Month</label>
            <Select
              style={{ width: '100%' }}
              value={monthKey}
              onChange={(v) => setMonthKey(v || 'all')}
              options={MONTHS_FY}
              getPopupContainer={(trigger) => trigger.parentNode}
            />
          </div>
          

        </div>
      </div>

      {/* Operational Cost Table */}
      <div className="table-container">
        {loading ? (
          <div className="loading">Loading operational costs...</div>
        ) : (
          <>
            <div className="table-header">
              <h3>Operational Cost ({formatIndianNumber(pagination.totalItems || 0, 0)} total)</h3>
            </div>
            
            <div className="table-wrapper">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Category</th>
                    <th>Name</th>
                    <th>Facility</th>
                    <th>Service Month</th>
                    <th>Amount</th>
                    <th>Created By</th>
                    <th>Created At</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {expenses.map(expense => (
                    <tr key={expense._id}>
                      <td>
                        <span className="category-badge" style={{ whiteSpace: 'normal' }}>
                          {expense.category?.name || '-'}
                        </span>
                      </td>
                      <td>{expense.name || '-'}</td>
                      <td>{expense.facility || '-'}</td>
                      <td>
                        {expense.serviceMonth ? 
                          ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'].includes(expense.serviceMonth) ?
                            ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'][['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'].indexOf(expense.serviceMonth)] :
                            expense.serviceMonth
                          : '-'
                        }
                      </td>
                      <td className="amount-cell">
                        {formatIndianCurrency(expense.amount || 0)}
                      </td>
                      <td>{expense.createdBy?.name || '-'}</td>
                      <td>{expense.createdAt ? format(new Date(expense.createdAt), 'dd/MM/yyyy') : '-'}</td>
                      <td>
                        {currentUser?.role !== 'viewer' && (
                          <div className="action-buttons">
                            <button
                              onClick={() => openEditModal(expense)}
                              className="btn-icon btn-edit"
                              title="Edit"
                            >
                              <Edit size="1em" />
                            </button>
                            <button
                              onClick={() => handleDelete(expense._id)}
                              className="btn-icon btn-delete"
                              title="Delete"
                            >
                              <Trash size="1em" />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="pagination">
                <button
                  onClick={() => setPagination(prev => ({ ...prev, currentPage: prev.currentPage - 1 }))}
                  disabled={pagination.currentPage === 1}
                  className="btn btn-outline"
                >
                  Previous
                </button>
                <span className="pagination-info">
                  Page {pagination.currentPage} of {pagination.totalPages}
                </span>
                <button
                  onClick={() => setPagination(prev => ({ ...prev, currentPage: prev.currentPage + 1 }))}
                  disabled={pagination.currentPage === pagination.totalPages}
                  className="btn btn-outline"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Add/Edit Expense Modal */}
      {showAddModal && (
        <div className="modal-right-overlay">
          <div className="modal-right-content" onClick={e => e.stopPropagation()}>
            <div className="modal-right-header">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600', color: '#333' }}>
                  {editingExpense ? 'Edit Operational Cost' : 'Add new Operational cost'}
                </h2>
                <button 
                  className="btn" 
                  style={{ background: 'none', border: 'none', fontSize: '1.5rem', color: '#888', cursor: 'pointer', padding: '4px' }} 
                  onClick={() => setShowAddModal(false)} 
                  aria-label="Close"
                >
                  <X size={20} />
                </button>
              </div>
            </div>
            
            <div className="modal-right-body">
              <form id="expense-form" onSubmit={handleSubmit}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                <div className="form-group">
                  <label>Expense Category *</label>
                  <Select
                    style={{ width: '100%' }}
                    value={formData.category || undefined}
                    onChange={(v) => handleFormChange('category', v || '')}
                    options={categories.map(c => ({ label: c, value: c }))}
                    placeholder="Select Category"
                    showSearch
                    optionFilterProp="label"
                    allowClear
                    getPopupContainer={(trigger) => trigger.parentNode}
                  />
                </div>

                <div className="form-group">
                  <label>Expense Name *</label>
                  <Input
                    value={formData.name}
                    onChange={(e) => handleFormChange('name', e.target.value)}
                    placeholder="Enter expense name"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Invoice Date</label>
                  <DatePicker
                    style={{ width: '100%' }}
                    value={formData.invoiceDate ? dayjs(formData.invoiceDate) : null}
                    onChange={(d) => handleFormChange('invoiceDate', d ? d.format('YYYY-MM-DD') : '')}
                    format="DD/MM/YYYY"
                    placeholder="dd/mm/yyyy"
                    allowClear
                  />
                </div>

                <div className="form-group">
                  <label>Invoice Number</label>
                  <Input
                    value={formData.invoiceNumber}
                    onChange={(e) => handleFormChange('invoiceNumber', e.target.value)}
                    placeholder="Enter invoice number"
                  />
                </div>

                <div className="form-group">
                  <label>Facility *</label>
                  <Select
                    style={{ width: '100%' }}
                    value={formData.facility || undefined}
                    onChange={(v) => handleFormChange('facility', v || '')}
                    options={facilities.map(f => ({ label: f.name, value: f.name }))}
                    placeholder="Select Facility"
                    showSearch
                    optionFilterProp="label"
                    allowClear
                    getPopupContainer={(trigger) => trigger.parentNode}
                  />
                </div>

                <div className="form-group">
                  <label>Service Month</label>
                  <Select
                    style={{ width: '100%' }}
                    value={formData.serviceMonth || undefined}
                    onChange={(v) => handleFormChange('serviceMonth', v || '')}
                    options={[
                      { label: 'January', value: 'jan' },
                      { label: 'February', value: 'feb' },
                      { label: 'March', value: 'mar' },
                      { label: 'April', value: 'apr' },
                      { label: 'May', value: 'may' },
                      { label: 'June', value: 'jun' },
                      { label: 'July', value: 'jul' },
                      { label: 'August', value: 'aug' },
                      { label: 'September', value: 'sep' },
                      { label: 'October', value: 'oct' },
                      { label: 'November', value: 'nov' },
                      { label: 'December', value: 'dec' }
                    ]}
                    placeholder="Select service month"
                    allowClear
                    getPopupContainer={(trigger) => trigger.parentNode}
                  />
                </div>

                <div className="form-group">
                  <label>Amount *</label>
                  <InputNumber
                    style={{ width: '100%' }}
                    step={0.01}
                    min={0}
                    value={formData.amount === '' ? null : Number(formData.amount)}
                    onChange={(val) => handleFormChange('amount', val ?? '')}
                    placeholder="Enter amount"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Description</label>
                  <Input.TextArea
                    value={formData.description}
                    onChange={(e) => handleFormChange('description', e.target.value)}
                    placeholder="Enter description (optional)"
                    rows={3}
                  />
                </div>
              </div>
            </form>
            </div>
            <div className="modal-right-footer">
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => setShowAddModal(false)} className="btn btn-secondary">
                  Cancel
                </button>
                <button type="submit" form="expense-form" className="btn btn-primary">
                  {editingExpense ? 'Update Operational Cost' : 'Add Operational Cost'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Excel Upload Modal */}
      {showUploadModal && (
        <div className="modal-right-overlay">
          <div className="modal-right-content" onClick={e => e.stopPropagation()}>
            <div className="modal-right-header">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600', color: '#333' }}>Bulk Upload Operational Cost</h2>
                <button 
                  className="btn" 
                  style={{ background: 'none', border: 'none', fontSize: '1.5rem', color: '#888', cursor: 'pointer', padding: '4px' }} 
                  onClick={() => setShowUploadModal(false)} 
                  aria-label="Close"
                >
                  <X size={20} />
                </button>
              </div>
            </div>
            <div className="modal-right-body">
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                  <button className="btn btn-secondary" type="button" onClick={downloadTemplate}>
                    <Download size="1.1em" />
                    Download Template
                  </button>
                  <label className="btn btn-primary" style={{ cursor: 'pointer' }}>
                    <Upload size="1.1em" />
                    Choose File
                    <input type="file" accept=".xlsx,.xls,.csv" style={{ display: 'none' }} onChange={(e) => handleFileSelected(e.target.files?.[0])} />
                  </label>
                  {uploadFileName ? <span style={{ alignSelf: 'center', color: '#666' }}>{uploadFileName}</span> : null}
                </div>
                <div onDragOver={onDragOver} onDrop={onDropUpload} style={{ border: '2px dashed #bbb', borderRadius: '8px', padding: '24px', textAlign: 'center', color: '#666', background: '#fafafa' }}>
                  Drag and drop your Excel file here
                </div>
                {isParsing && (<div className="loading">Parsing file...</div>)}
                {uploadErrors.length > 0 && (
                  <div style={{ color: '#b00020' }}>
                    {uploadErrors.map((e, i) => (<div key={i}>- {e}</div>))}
                  </div>
                )}
                {(parsedRows.length > 0 || invalidRows.length > 0) && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                      <span><strong>Valid rows:</strong> {parsedRows.length}</span>
                      <span><strong>Invalid rows:</strong> {invalidRows.length}</span>
                    </div>
                    {invalidRows.length > 0 && (
                      <div style={{ maxHeight: '120px', overflowY: 'auto', background: '#fff7f7', border: '1px solid #ffd6d6', padding: '8px', borderRadius: '6px' }}>
                        {invalidRows.slice(0, 10).map((ir, i) => (
                          <div key={i} style={{ marginBottom: '6px' }}>
                            <div style={{ fontWeight: 600 }}>Row {ir.index}:</div>
                            <div style={{ color: '#b00020' }}>{ir.errors.join(', ')}</div>
                          </div>
                        ))}
                        {invalidRows.length > 10 ? <div>...and {invalidRows.length - 10} more</div> : null}
                      </div>
                    )}
                  </div>
                )}
                {isImporting && (
                  <div>
                    Importing {importProgress.done}/{importProgress.total}...
                  </div>
                )}
              </div>
            </div>
            <div className="modal-right-footer">
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => setShowUploadModal(false)} className="btn btn-secondary" disabled={isImporting}>
                  Cancel
                </button>
                <button type="button" onClick={startImport} className="btn btn-primary" disabled={isParsing || isImporting || parsedRows.length === 0}>
                  {isImporting ? 'Importing...' : `Import ${parsedRows.length} rows`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Expenses;
